import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import Position from '../../types/enum/Position';
import Popover from '../Popover/Popover';
import './Menu.scss';
import IMenuProps from './MenuProps';

const Menu = ({
  trigger,
  content,
  offset = 4,
  width = '200px',
  position = Position.BOTTOM_START,
  onOpen,
  onClose,
  disabled,
  className,
  allowPropagation,
}: IMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const target = useRef<HTMLElement>();
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
    } else {
      if (isOpen) {
        handleOnOpen();
        addEventListeners();
      } else {
        handleOnClose();
        removeEventListeners();
      }
    }

    return () => {
      removeEventListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function addEventListeners() {
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('touchstart', handleDocumentClick);
  }

  function removeEventListeners() {
    document.removeEventListener('click', handleDocumentClick);
    document.removeEventListener('touchstart', handleDocumentClick);
  }

  function handleOnOpen() {
    if (onOpen) {
      onOpen();
    }
  }

  function handleOnClose() {
    if (onClose) {
      onClose();
    }
  }

  function handleClick(event: MouseEvent) {
    allowPropagation ? setIsOpen(!isOpen) : (event.stopPropagation(), event.preventDefault());

    if (!disabled && !allowPropagation) {
      setIsOpen(!isOpen);
    }
  }

  function handleDocumentClick(event: MouseEvent | TouchEvent) {
    if (isOpen) {
      if (event.target && (event.target as HTMLElement).closest('.menu-content')) {
        return;
      }
      closeMenu();
    }
  }

  function closeMenu() {
    setIsOpen(false);
    document.removeEventListener('click', handleDocumentClick);
    document.removeEventListener('touchstart', handleDocumentClick);
  }

  // Adds necessary ref and properties to trigger element
  function renderTrigger() {
    const trig = trigger((el) => (target.current = el));
    const trigClasses = classNames(
      {
        'menu-trigger': true,
        'menu-disabled': disabled,
        'is-active': isOpen,
      },
      trig.props.className
    );

    const options = {
      onClick: handleClick,
      className: trigClasses,
    };

    return React.cloneElement(trig, options);
  }

  function getTargetNode() {
    if (target.current) {
      return target.current;
    } else {
      return undefined;
    }
  }

  const classes = classNames(
    {
      'menu-shell': true,
      [`menu-${position}`]: true,
    },
    className
  );

  return (
    <>
      {renderTrigger()}
      <Popover
        isOpen={isOpen}
        transitionName="slide-open"
        position={position}
        getTargetNode={getTargetNode}
        offset={offset}
      >
        <div className={classes}>
          <div className="menu-content anim" style={{ width }}>
            {content(closeMenu)}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default Menu;
